import { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from 'react';

interface AccionData {
  codigoUnico: string;
  orden: number;
  tipo: string;
  tipoDisplay: string;
  texto: string;
  padreId?: string;
  saltarA?: string;
  finaliza: boolean;
  alternativaOpciones?: Array<{ texto: string; saltarA?: string; finaliza?: boolean }>;
  postaGroups?: string[];
  postaMensaje?: string;
  isGroup: boolean;
}

interface Jump {
  from: string;
  to: string;
  label?: string;
  color: string;
}

interface OutlineViewProps {
  onClose?: () => void;
}

const TIPO_DISPLAY: Record<string, string> = {
  TextoTipe: 'Texto',
  NumeroTipe: 'Número',
  FechaTipe: 'Fecha',
  HoraTipe: 'Hora',
  FotoTipe: 'Foto',
  DibujoTipe: 'Dibujo',
  QrTipe: 'QR',
  ValorizacionTipe: 'Valorización',
  SolicitudDeArchivoTipe: 'Archivo',
  AlternativaTipe: 'Alternativa',
  AlternativaSmTipe: 'Alternativa Sm',
  ContadorTipe: 'Contador',
  InventarioTipe: 'Inventario',
  EncabezadoTipe: 'Encabezado',
  SliderTipe: 'Slider',
  DropdownTipe: 'Dropdown',
  AudioTipe: 'Audio',
  VideoTipe: 'Video',
  TablaTipe: 'Tabla',
  AritmeticaTipe: 'Aritmética',
  NotaTipe: 'Nota',
  SeccionTipe: 'Sección',
  CheckpointTipe: 'Checkpoint',
  RequiereLocalizacionTipe: 'Localización',
  PostaTipe: 'Posta',
  ApruebaExternoTipe: 'Aprueba Externo',
  GrupoDeAccionesTipe: 'Grupo de acciones',
};

function resolveJumpTargetFromAcciones(saltarA: string, codigos: Set<string>): string | null {
  if (!saltarA) return null;
  const s = String(saltarA);
  if (codigos.has(s)) return s;
  if (s.includes('-')) {
    const base = s.split('-')[0];
    if (codigos.has(base)) return base;
  }
  return null;
}

function extractFromDOM(): AccionData[] {
  const lista = document.getElementById('ListaAccionesOrden');
  if (!lista) return [];

  const lis = Array.from(lista.querySelectorAll('li.list-group-item')) as HTMLElement[];
  const result: AccionData[] = [];
  const seen = new Set<string>();
  let currentGroupPadre: string | undefined;

  lis.forEach(li => {
    if (li.classList.contains('accionVacia')) return;
    const itemId = li.id || '';

    // Group closing — resets padre
    if (itemId.includes('GrupoDeAccionesTipeF')) {
      currentGroupPadre = undefined;
      return;
    }

    const codigoEl = li.querySelector('.CodigoUnico') as HTMLElement;
    if (!codigoEl || !codigoEl.id) return;
    const codigoUnico = codigoEl.id;
    if (seen.has(codigoUnico)) return;
    seen.add(codigoUnico);

    const tituloEl = li.querySelector('.TituloAccion') as HTMLInputElement;
    let texto = tituloEl?.value || '';
    if (itemId.includes('RequiereLocalizacion')) {
      const geo = li.querySelector('#geocomplete') as HTMLInputElement;
      if (geo?.value) texto = geo.value;
    }

    const ordenEl = li.querySelector('.NumeroOrdenAccion') as HTMLElement;
    const orden = ordenEl ? parseInt(ordenEl.textContent || '0', 10) : 0;

    const isGroup = itemId.includes('GrupoDeAccionesTipe') && !itemId.includes('GrupoDeAccionesTipeF');
    const esHijo = li.classList.contains('EsDeGrupoDeAcciones');
    const padreId = esHijo ? currentGroupPadre : undefined;

    // Flow control
    let saltarA: string | undefined;
    let finaliza = false;
    const selected = li.querySelectorAll('.celdaOpcionAccionSelected');
    const mainSelected = Array.from(selected).find(
      opt => !opt.closest('.InputsAlternativasTexto2.existeAlternativa')
    );
    if (mainSelected) {
      const p = mainSelected.closest('a');
      if (p?.id === 'O3') finaliza = true;
      else if (p?.id === 'O2') {
        const containers = li.querySelectorAll('.OpcionesSaltarAContenedor .OpcionSeleccionada');
        const mainContainer = Array.from(containers).find(
          c => !c.closest('.InputsAlternativasTexto2.existeAlternativa')
        );
        if (mainContainer) {
          const link = mainContainer.querySelector('a.OpcionesSaltarA') as HTMLElement;
          if (link?.id && !link.id.startsWith('_')) saltarA = link.id;
        }
      }
    }

    // Alternativa options
    let alternativaOpciones: AccionData['alternativaOpciones'];
    if (itemId.includes('Alternativa')) {
      alternativaOpciones = [];
      const altDivs = li.querySelectorAll('.InputsAlternativasTexto2.existeAlternativa');
      altDivs.forEach(altDiv => {
        const altInput = altDiv.querySelector('.TituloAlternativa') as HTMLInputElement;
        const altTexto = altInput?.value || '';
        let altSaltarA: string | undefined;
        let altFinaliza = false;
        const altSelected = altDiv.querySelector('.celdaOpcionAccionSelected');
        if (altSelected) {
          const altP = altSelected.closest('a');
          if (altP?.id === 'O3') altFinaliza = true;
          else if (altP?.id === 'O2') {
            const altContainer = altDiv.querySelector('.OpcionesSaltarAContenedor .OpcionSeleccionada');
            if (altContainer) {
              const altLink = altContainer.querySelector('a.OpcionesSaltarA') as HTMLElement;
              if (altLink?.id && !altLink.id.startsWith('_')) altSaltarA = altLink.id;
            }
          }
        }
        alternativaOpciones!.push({ texto: altTexto, saltarA: altSaltarA, finaliza: altFinaliza });
      });
    }

    // Posta groups (transfer targets)
    let postaGroups: string[] | undefined;
    let postaMensaje: string | undefined;
    if (itemId.includes('Posta')) {
      const grp = li.querySelector('textarea.NotificarGrupos') as HTMLTextAreaElement;
      if (grp?.value) {
        try {
          const tags = JSON.parse(grp.value);
          if (Array.isArray(tags)) postaGroups = tags.map((t: { value?: string }) => t.value || '').filter(Boolean);
        } catch {
          const c = li.querySelector('.tagify.NotificarGrupos');
          if (c) postaGroups = Array.from(c.querySelectorAll('.tagify__tag-text')).map(e => e.textContent || '').filter(Boolean);
        }
      }
      const msgEl = li.querySelector('.MensajeDeNotificacionInput, input.MensajeDeNotificacion') as HTMLInputElement;
      postaMensaje = msgEl?.value || undefined;
    }

    // Determine tipoDisplay
    let tipoKey = itemId;
    if (itemId.includes('GrupoDeAcciones')) tipoKey = 'GrupoDeAccionesTipe';
    const tipoDisplay = TIPO_DISPLAY[tipoKey] || itemId.replace('Tipe', '');

    result.push({
      codigoUnico,
      orden,
      tipo: itemId,
      tipoDisplay,
      texto: texto || `Acción ${orden}`,
      padreId,
      saltarA,
      finaliza,
      alternativaOpciones,
      postaGroups,
      postaMensaje,
      isGroup,
    });

    if (isGroup) currentGroupPadre = codigoUnico;
  });

  return result;
}

function typeBadgeColor(tipo: string): string {
  if (tipo.includes('Checkpoint')) return '#dbeafe,#1e40af';
  if (tipo.includes('GrupoDeAcciones')) return '#fef3c7,#92400e';
  if (tipo.includes('Encabezado')) return '#e0e7ff,#3730a3';
  if (tipo.includes('Alternativa')) return '#fce7f3,#9d174d';
  if (tipo.includes('Posta')) return '#ede9fe,#6d28d9';
  if (tipo.includes('Seccion')) return '#fce7f3,#9d174d';
  if (tipo.includes('Nota')) return '#fef9c3,#854d0e';
  return '#f1f5f9,#475569';
}

function numBadgeColor(tipo: string): string {
  if (tipo.includes('Checkpoint')) return '#3b82f6';
  if (tipo.includes('GrupoDeAcciones')) return '#f59e0b';
  if (tipo.includes('Encabezado')) return '#6366f1';
  if (tipo.includes('Alternativa')) return '#ec4899';
  if (tipo.includes('Posta')) return '#8b5cf6';
  if (tipo.includes('Seccion')) return '#ec4899';
  if (tipo.includes('Nota')) return '#eab308';
  if (tipo.includes('Finaliza') || /FIN/i.test(tipo)) return '#ef4444';
  return '#94a3b8';
}

export default function OutlineView({ onClose }: OutlineViewProps) {
  const [acciones, setAcciones] = useState<AccionData[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const [lines, setLines] = useState<Array<{ d: string; color: string; id: string }>>([]);
  const [highlighted, setHighlighted] = useState<string | null>(null);

  const reload = useCallback(() => {
    setAcciones(extractFromDOM());
  }, []);

  const scrollToTarget = useCallback((codigo: string) => {
    if (!containerRef.current) return;
    const el = containerRef.current.querySelector(`[data-codigo="${codigo}"]`) as HTMLElement | null;
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlighted(codigo);
    setTimeout(() => setHighlighted(null), 1800);
  }, []);

  useEffect(() => {
    reload();
    const handler = () => reload();
    window.addEventListener('acciones-updated', handler);
    return () => window.removeEventListener('acciones-updated', handler);
  }, [reload]);

  // Compute jumps + branch ownership (first source wins as "owner" to render branch tree)
  const { jumps, roots, branchOwnerOf, branchChildrenOf } = useMemo(() => {
    const codigos = new Set(acciones.map(a => a.codigoUnico));
    const j: Jump[] = [];
    const owner = new Map<string, string>(); // target → source (first saltarA wins)
    acciones.forEach(a => {
      if (a.saltarA) {
        const t = resolveJumpTargetFromAcciones(a.saltarA, codigos);
        if (t) {
          j.push({ from: a.codigoUnico, to: t, color: '#f59e0b' });
          if (!owner.has(t)) owner.set(t, a.codigoUnico);
        }
      }
      if (a.alternativaOpciones) {
        a.alternativaOpciones.forEach((opt, idx) => {
          if (opt.saltarA) {
            const t = resolveJumpTargetFromAcciones(opt.saltarA, codigos);
            if (t) {
              j.push({ from: a.codigoUnico, to: t, color: '#8b5cf6', label: String.fromCharCode(65 + idx) });
              if (!owner.has(t)) owner.set(t, a.codigoUnico);
            }
          }
        });
      }
    });

    const branches = new Map<string, AccionData[]>();
    owner.forEach((sourceCodigo, targetCodigo) => {
      const targetA = acciones.find(a => a.codigoUnico === targetCodigo);
      if (targetA) {
        if (!branches.has(sourceCodigo)) branches.set(sourceCodigo, []);
        branches.get(sourceCodigo)!.push(targetA);
      }
    });

    // Main roots: no padre AND not a branch target owned elsewhere
    const rootList = acciones.filter(a => !a.padreId && !owner.has(a.codigoUnico));
    return { jumps: j, roots: rootList, branchOwnerOf: owner, branchChildrenOf: branches };
  }, [acciones]);

  const childrenByParent = useMemo(() => {
    const m = new Map<string, AccionData[]>();
    acciones.forEach(a => {
      if (a.padreId) {
        if (!m.has(a.padreId)) m.set(a.padreId, []);
        m.get(a.padreId)!.push(a);
      }
    });
    return m;
  }, [acciones]);

  void branchOwnerOf; // suppress unused warning — used transitively via roots filter

  // Compute SVG lines after layout — L-shaped routes on the right side with lane assignment
  useLayoutEffect(() => {
    if (!containerRef.current || jumps.length === 0) {
      setLines([]);
      return;
    }
    const container = containerRef.current;
    const crect = container.getBoundingClientRect();
    const positions = new Map<string, { top: number; bottom: number; right: number }>();
    acciones.forEach(a => {
      const el = container.querySelector(`[data-codigo="${a.codigoUnico}"]`) as HTMLElement | null;
      if (el) {
        const r = el.getBoundingClientRect();
        positions.set(a.codigoUnico, {
          top: r.top - crect.top + container.scrollTop,
          bottom: r.bottom - crect.top + container.scrollTop,
          right: r.right - crect.left + container.scrollLeft,
        });
      }
    });

    // Sort by span length (short first) for better lane packing
    const sorted = [...jumps].map((j, i) => ({ ...j, idx: i }));
    sorted.sort((a, b) => {
      const sa = positions.get(a.from)?.bottom ?? 0;
      const sb = positions.get(b.from)?.bottom ?? 0;
      return sa - sb;
    });

    // Greedy lane assignment based on vertical span
    const lanes: Array<Array<{ top: number; bottom: number }>> = [];
    const laneOf = new Map<number, number>();
    sorted.forEach((jj, i) => {
      const src = positions.get(jj.from);
      const tgt = positions.get(jj.to);
      if (!src || !tgt) return;
      const top = Math.min(src.bottom, tgt.top);
      const bottom = Math.max(src.bottom, tgt.top);
      let lane = 0;
      while (lane < lanes.length) {
        const overlap = lanes[lane].some(s => !(bottom < s.top || top > s.bottom));
        if (!overlap) break;
        lane++;
      }
      if (lane === lanes.length) lanes.push([]);
      lanes[lane].push({ top, bottom });
      laneOf.set(i, lane);
    });

    const LANE_GAP = 24;
    const LANE_START_OFFSET = 20;

    const newLines = sorted.map((jj, i) => {
      const src = positions.get(jj.from);
      const tgt = positions.get(jj.to);
      if (!src || !tgt) return null;
      const lane = laneOf.get(i) ?? 0;
      const sourceX = src.right;
      const targetX = tgt.right;
      const laneX = Math.max(sourceX, targetX) + LANE_START_OFFSET + lane * LANE_GAP;
      const y1 = (src.top + src.bottom) / 2;
      const y2 = (tgt.top + tgt.bottom) / 2;
      const r = 8; // corner radius
      const dir = y2 > y1 ? 1 : -1;
      // Path: from source right edge → horizontal to laneX → vertical to target Y → horizontal back to target right edge
      const d = [
        `M ${sourceX} ${y1}`,
        `H ${laneX - r}`,
        `Q ${laneX} ${y1}, ${laneX} ${y1 + r * dir}`,
        `V ${y2 - r * dir}`,
        `Q ${laneX} ${y2}, ${laneX - r} ${y2}`,
        `H ${targetX + 4}`,
      ].join(' ');
      return { d, color: jj.color, id: `${jj.from}->${jj.to}-${i}` };
    }).filter((x): x is { d: string; color: string; id: string } => x !== null);
    setLines(newLines);
  }, [acciones, jumps]);

  const codigosSet = useMemo(() => new Set(acciones.map(a => a.codigoUnico)), [acciones]);
  const accionByCodigo = useMemo(() => {
    const m = new Map<string, AccionData>();
    acciones.forEach(a => m.set(a.codigoUnico, a));
    return m;
  }, [acciones]);

  const branchChip = (targetCodigo: string | undefined, color: string) => {
    if (!targetCodigo) return null;
    const t = resolveJumpTargetFromAcciones(targetCodigo, codigosSet);
    if (!t) return null;
    const targetA = accionByCodigo.get(t);
    if (!targetA) return null;
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); scrollToTarget(t); }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          background: color,
          color: '#fff',
          border: 'none',
          borderRadius: 10,
          padding: '1px 8px 1px 4px',
          fontSize: 10,
          fontWeight: 600,
          cursor: 'pointer',
          lineHeight: 1.4,
        }}
        title={`Saltar a: ${targetA.texto}`}
      >
        <span style={{ background: 'rgba(255,255,255,0.3)', borderRadius: 6, padding: '0 5px', fontSize: 9 }}>#{targetA.orden}</span>
        <span style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{targetA.texto}</span>
      </button>
    );
  };

  const renderRow = (a: AccionData, depth: number) => {
    const [bg, fg] = typeBadgeColor(a.tipo).split(',');
    const numBg = numBadgeColor(a.tipo);
    const isHL = highlighted === a.codigoUnico;
    return (
      <div key={a.codigoUnico} data-codigo={a.codigoUnico} style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '10px 14px',
        marginLeft: depth * 28,
        marginBottom: 6,
        background: isHL ? '#fef3c7' : '#fff',
        border: `1px solid ${isHL ? '#f59e0b' : '#e5e7eb'}`,
        borderRadius: 8,
        boxShadow: isHL ? '0 0 0 3px rgba(245,158,11,0.25)' : '0 1px 2px rgba(0,0,0,0.04)',
        transition: 'background 0.3s, border-color 0.3s',
      }}>
        <div style={{
          flexShrink: 0,
          width: 28,
          height: 28,
          borderRadius: 6,
          background: numBg,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 12,
          fontWeight: 700,
        }}>{a.orden}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{a.texto}</span>
            <span style={{
              background: bg,
              color: fg,
              fontSize: 10,
              fontWeight: 600,
              padding: '2px 8px',
              borderRadius: 10,
              textTransform: 'uppercase',
              letterSpacing: 0.3,
            }}>{a.tipoDisplay}</span>
            {a.finaliza && (
              <span style={{ background: '#fef2f2', color: '#b91c1c', fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10 }}>FIN</span>
            )}
            {a.saltarA && !a.alternativaOpciones && branchChip(a.saltarA, '#f59e0b')}
          </div>
          {a.alternativaOpciones && a.alternativaOpciones.length > 0 && (
            <ul style={{ listStyle: 'none', padding: 0, margin: '6px 0 0', display: 'flex', flexDirection: 'column', gap: 3 }}>
              {a.alternativaOpciones.map((opt, i) => (
                <li key={i} style={{ fontSize: 11, color: '#475569', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ width: 16, height: 16, borderRadius: '50%', background: '#8b5cf6', color: '#fff', fontSize: 9, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {String.fromCharCode(65 + i)}
                  </span>
                  <span style={{ flex: '1 1 auto', minWidth: 0 }}>{opt.texto || `Opción ${i + 1}`}</span>
                  {opt.saltarA && branchChip(opt.saltarA, '#8b5cf6')}
                  {opt.finaliza && <span style={{ background: '#fef2f2', color: '#b91c1c', fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 8 }}>FIN</span>}
                </li>
              ))}
            </ul>
          )}
          {a.postaGroups && a.postaGroups.length > 0 && (
            <div style={{ marginTop: 6, fontSize: 11, color: '#6d28d9' }}>
              Transferir a: {a.postaGroups.join(', ')}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderNode = (a: AccionData, depth: number, visitedBranches: Set<string> = new Set()): React.ReactNode => {
    const kids = childrenByParent.get(a.codigoUnico) || [];
    const branchTargets = (branchChildrenOf.get(a.codigoUnico) || []).filter(b => !visitedBranches.has(b.codigoUnico));
    return (
      <div key={a.codigoUnico}>
        {renderRow(a, depth)}
        {kids.length > 0 && (
          <div style={{ borderLeft: '2px solid #fcd34d', marginLeft: depth * 28 + 14 }}>
            {kids.map(k => renderNode(k, depth + 1, visitedBranches))}
          </div>
        )}
        {branchTargets.length > 0 && (
          <div style={{
            display: 'flex',
            gap: 12,
            marginLeft: depth * 28 + 14,
            marginTop: 4,
            marginBottom: 8,
            alignItems: 'flex-start',
          }}>
            {branchTargets.map((bt, bidx) => {
              const nextVisited = new Set(visitedBranches);
              nextVisited.add(bt.codigoUnico);
              const sourceOpt = a.alternativaOpciones?.findIndex(opt => {
                if (!opt.saltarA) return false;
                return resolveJumpTargetFromAcciones(opt.saltarA, codigosSet) === bt.codigoUnico;
              }) ?? -1;
              const optLetter = sourceOpt >= 0 ? String.fromCharCode(65 + sourceOpt) : null;
              const branchColor = optLetter ? '#8b5cf6' : '#f59e0b';
              return (
                <div key={`branch-${bt.codigoUnico}-${bidx}`} style={{
                  flex: 1,
                  minWidth: 240,
                  borderTop: `3px solid ${branchColor}`,
                  borderRadius: '0 0 8px 8px',
                  paddingTop: 8,
                  paddingLeft: 6,
                  paddingRight: 6,
                  background: optLetter ? 'rgba(139,92,246,0.04)' : 'rgba(245,158,11,0.04)',
                }}>
                  <div style={{
                    fontSize: 10,
                    color: optLetter ? '#6d28d9' : '#b45309',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 0.6,
                    marginBottom: 6,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}>
                    {optLetter && (
                      <span style={{
                        width: 16, height: 16, borderRadius: '50%', background: '#8b5cf6', color: '#fff',
                        fontSize: 9, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      }}>{optLetter}</span>
                    )}
                    Rama {optLetter ? `opción ${optLetter}` : 'saltar'}
                  </div>
                  {renderNode(bt, 0, nextVisited)}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', background: '#f8fafc', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 48,
        background: '#fff',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 16px',
        zIndex: 10,
      }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>Flujo del formulario</span>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 10, fontSize: 11, color: '#475569' }}>
            <span><span style={{ display: 'inline-block', width: 10, height: 3, background: '#f59e0b', borderRadius: 2, marginRight: 4, verticalAlign: 'middle' }} />Saltar a</span>
            <span><span style={{ display: 'inline-block', width: 10, height: 3, background: '#8b5cf6', borderRadius: 2, marginRight: 4, verticalAlign: 'middle' }} />Alternativa</span>
          </div>
          {onClose && (
            <button onClick={onClose} style={{
              background: 'transparent', border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 12, color: '#475569',
            }}>Cerrar</button>
          )}
        </div>
      </div>

      {/* Content */}
      <div ref={containerRef} style={{
        position: 'absolute',
        top: 48,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'auto',
        padding: '20px 20px 20px 40px',
      }}>
        {/* SVG jump lines overlay — positioned over entire content area, curves route right of rows */}
        <svg style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          overflow: 'visible',
        }}>
          <defs>
            <marker id="arrow-orange" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#f59e0b" />
            </marker>
            <marker id="arrow-purple" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#8b5cf6" />
            </marker>
          </defs>
          {lines.map(l => (
            <path
              key={l.id}
              d={l.d}
              stroke={l.color}
              strokeWidth="1.8"
              fill="none"
              markerEnd={l.color === '#f59e0b' ? 'url(#arrow-orange)' : 'url(#arrow-purple)'}
              opacity="0.85"
            />
          ))}
        </svg>

        {roots.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
            Formulario vacío
          </div>
        ) : (
          <div style={{ maxWidth: 900 }}>
            {roots.map(r => renderNode(r, 0))}
          </div>
        )}
      </div>
    </div>
  );
}
